import { useLazyQuery } from '@apollo/client'
import { GetOfficeMasterPublishProductFamilyStatusQuery } from 'api/graphQL/queries/project.queries'
import {
    GetBackendProcessStatusInput,
    GetOfficeMasterPublishProductFamilyStatusResponse,
    PublishResult,
} from 'api/graphQL/types/backendProcess.types'
import { useCallback, useEffect, useRef } from 'react'

const STATUS_IN_PROGRESS = 'INPROGRESS'
const STATUS_COMPLETED = 'COMPLETE'
const STATUS_ERROR = 'ERROR'
const STATUS_QUEUED = 'QUEUED'

interface UsePublishStatusPollerProps {
    instanceId: string | undefined
    isPublishInProgress: boolean
    onComplete: (output: PublishResult) => void
    onError: (errorMessage: string | null | undefined) => void
    onProgress?: (progressPercentage: number) => void
}

export const usePublishStatusPoller = ({
    instanceId,
    isPublishInProgress,
    onComplete,
    onError,
    onProgress,
}: UsePublishStatusPollerProps) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const isMountedRef = useRef(true)
    const retryCountRef = useRef(0)
    const MAX_RETRIES = 3

    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    const queryForStatusRef = useRef<(() => void) | null>(null)

    const onStatusQueryCompleted = useCallback(
        (data: GetOfficeMasterPublishProductFamilyStatusResponse) => {
            const publishStatus =
                data?.getOfficeMasterPublishProductFamilyStatus
            if (!publishStatus) {
                onError('No publish status received')
                return
            }

            const state = publishStatus.state?.toUpperCase()

            // Check for UNAVAILABLE status and retry
            if (state === 'UNAVAILABLE') {
                if (retryCountRef.current < MAX_RETRIES) {
                    retryCountRef.current++
                    if (isMountedRef.current && queryForStatusRef.current) {
                        timeoutRef.current = setTimeout(
                            () => queryForStatusRef.current?.(),
                            2000 // Shorter delay for retries
                        )
                    }
                    return
                } else {
                    onError(
                        'Service temporarily unavailable. Maximum retry attempts reached.'
                    )
                    return
                }
            }

            // Reset retry count on successful response
            retryCountRef.current = 0

            // Report progress if callback is provided
            if (onProgress) {
                onProgress(publishStatus.progressPercentage)
            }

            switch (state) {
                case STATUS_QUEUED:
                case STATUS_IN_PROGRESS:
                    // Still in progress; poll again after a short time
                    if (isMountedRef.current && queryForStatusRef.current) {
                        timeoutRef.current = setTimeout(
                            () => queryForStatusRef.current?.(),
                            5000
                        )
                    }
                    break
                case STATUS_COMPLETED:
                    if (publishStatus.output) {
                        onComplete(publishStatus.output)
                    } else {
                        onError('Publish completed but no output received')
                    }
                    break
                case STATUS_ERROR:
                    onError(
                        publishStatus.errorMessage ||
                            publishStatus.output?.errorMessage ||
                            'Publish failed with unknown error'
                    )
                    break
                default:
                    // Unknown status, stop polling
                    onError(`Unknown publish status: ${state}`)
                    break
            }
        },
        [onComplete, onError, onProgress]
    )

    const onStatusQueryError = useCallback(
        (error: any) => {
            // Check if it's a 503 error
            const is503Error =
                error?.networkError?.statusCode === 503 ||
                error?.message?.includes('503') ||
                error?.graphQLErrors?.some((err: any) =>
                    err?.message?.includes('503')
                )

            if (is503Error) {
                if (retryCountRef.current < MAX_RETRIES) {
                    retryCountRef.current++
                    if (isMountedRef.current && queryForStatusRef.current) {
                        timeoutRef.current = setTimeout(
                            () => queryForStatusRef.current?.(),
                            2000 // Shorter delay for retries
                        )
                    }
                    return
                } else {
                    onError(
                        'Service temporarily unavailable (503). Maximum retry attempts reached.'
                    )
                    return
                }
            }

            // For non-503 errors, return the error message directly
            onError(`Failed to fetch publish status: ${error.message}`)
        },
        [onError]
    )

    const [fetchPublishStatus] = useLazyQuery<
        GetOfficeMasterPublishProductFamilyStatusResponse,
        { input: GetBackendProcessStatusInput }
    >(GetOfficeMasterPublishProductFamilyStatusQuery, {
        onCompleted: onStatusQueryCompleted,
        onError: onStatusQueryError,
        fetchPolicy: 'network-only',
    })

    const queryForStatus = useCallback(() => {
        if (instanceId && isMountedRef.current) {
            fetchPublishStatus({
                variables: {
                    input: {
                        instanceId,
                    },
                },
            })
        }
    }, [instanceId, fetchPublishStatus])

    queryForStatusRef.current = queryForStatus

    useEffect(() => {
        if (instanceId && isPublishInProgress && isMountedRef.current) {
            queryForStatus()
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [instanceId, isPublishInProgress, queryForStatus])

    return {
        queryForStatus,
    }
}
